import { useEffect, useState, useRef, lazy, Suspense } from "react";
import "./App.css";
import { analyticsService } from "@/services/AnalyticsService";
import { Header, Footer } from "@/components/layout";
import { NotFoundRedirect, ProtectedRoute } from "@/components/common";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { LoadingFallback } from "@/components/ui/LoadingFallback";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { fetchAllMaps } from "@/features/maps/core/mapQueries";

import { CalculatorProvider } from "@/features/calculator/context/CalculatorContext";
import { SeoHead } from "@/components/common/SeoHead";
import { SchemaJSONLD } from "@/components/common/SchemaJSONLD";
import { Toaster } from "@/components/ui/Toaster";
import { CookieConsent } from "@/components/common/CookieConsent";
import { AnimatePresence, motion } from "framer-motion";
import { useSwipeNavigation } from "@/hooks/useSwipeNavigation";
import { Bot, Crown } from "lucide-react";
import { Modal } from "@/components/ui/Modal";


// Lazy load route components
const Calculator = lazy(() =>
  import("@/features/calculator/components/Calculator").then((module) => ({
    default: module.Calculator,
  })),
);
const LandingPage = lazy(() =>
  import("./pages/LandingPage").then((module) => ({
    default: module.LandingPage
  }))
);
const SavedMaps = lazy(() =>
  import("./pages/SavedMaps").then((module) => ({
    default: module.SavedMaps,
  })),
);
const CompareMaps = lazy(() =>
  import("./pages/CompareMaps").then((module) => ({
    default: module.CompareMaps,
  })),
);
const KnowledgeCenter = lazy(() =>
  import("./pages/KnowledgeCenter").then((module) => ({
    default: module.KnowledgeCenter,
  })),
);
const PrivacyPolicy = lazy(() =>
  import("./pages/PrivacyPolicy").then((module) => ({
    default: module.PrivacyPolicy,
  })),
);
const TermsOfUse = lazy(() =>
  import("./pages/TermsOfUse").then((module) => ({
    default: module.TermsOfUse,
  })),
);
const AccountSettings = lazy(() =>
  import("./pages/AccountSettings").then((module) => ({
    default: module.AccountSettings,
  })),
);

// Admin Features
const AdminLayout = lazy(() =>
  import("@/features/admin/components/AdminLayout").then((module) => ({
    default: module.default,
  }))
);
const CommandCenter = lazy(() =>
  import("@/features/admin/pages/CommandCenter").then((module) => ({
    default: module.default,
  }))
);
const ChatPage = lazy(() =>
  import("@/features/chat/pages/ChatPage").then((module) => ({
    default: module.default,
  }))
);





const SkyState = lazy(() =>
  import("./pages/SkyState").then((module) => ({
    default: module.SkyState,
  })),
);

// Minimal popup-close page — loaded by Grow's success_url inside the payment popup.
// window.close() works because the popup was originally opened by our window.open() call.
function PaymentSuccessPopup() {
  useEffect(() => {
    try { window.close(); } catch (_e) { /* browser security may block — safe to ignore */ }
    // Fallback: redirect to home if window.close() was blocked
    const t = setTimeout(() => { window.location.href = '/'; }, 2000);
    return () => clearTimeout(t);
  }, []);
  return null;
}
function App() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showChatTooltip, setShowChatTooltip] = useState(false);
  const [isPremiumLureOpen, setIsPremiumLureOpen] = useState(false);

  const lastNavTimeRef = useRef<number>(0);

  const location = useLocation();
  const { pathname } = location;

  // Global listener for premium lure dialog
  useEffect(() => {
    const handleOpenLure = () => {
      setIsPremiumLureOpen(true);
    };
    window.addEventListener('open-premium-lure', handleOpenLure);
    return () => {
      window.removeEventListener('open-premium-lure', handleOpenLure);
    };
  }, []);

  const isChatPage = pathname === "/chat";
  const { swipeHandlers, direction } = useSwipeNavigation();

  // Dynamic variants based on navigation direction (RTL aware)
  const pageVariants = {
    initial: (dir: number) => {
      if (isChatPage) return { opacity: 0, x: 0 };
      return {
        opacity: 0,
        x: dir > 0 ? "-100%" : "100%",
      };
    },
    animate: {
      opacity: 1,
      x: 0,
    },
    exit: (dir: number) => {
      if (isChatPage) return { opacity: 0, x: 0 };
      return {
        opacity: 0,
        x: dir > 0 ? "100%" : "-100%",
      };
    },
  };

  useEffect(() => {
    // Always scroll to top on route change
    window.scrollTo(0, 0);

    // Global Page View Tracking
    analyticsService.track('page_view', { path: pathname });
  }, [pathname]);


  // Prefetch maps when user logs in
  useEffect(() => {
    if (user?.id) {
      void queryClient.prefetchQuery({
        queryKey: ["maps", user.id],
        queryFn: () => fetchAllMaps(user.id),
        staleTime: Infinity,
      });
    }
  }, [user, queryClient]);

  return (

    <CalculatorProvider>
      <SeoHead />
      <div
        className="min-h-screen mystical-bg text-mystic-50 font-sans transition-colors duration-300 overflow-x-hidden"
        dir="rtl"
      >
        <Toaster />
        <CookieConsent />

        {/* Global Premium Teaser Marketing Lure Modal */}
        <Modal
          isOpen={isPremiumLureOpen}
          onClose={() => setIsPremiumLureOpen(false)}
          title=""
          maxWidth="max-w-md"
          compact={true}
        >
          <div className="relative overflow-hidden rounded-2xl bg-[#0d091a]/40 p-4 text-center" dir="rtl">
            {/* Gold orb background glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-gold-500/10 blur-[50px] rounded-full pointer-events-none" />
            
            <div className="relative z-10 flex flex-col items-center">
              <div className="mb-4 p-3 rounded-full bg-gold-500/10 border border-gold-500/20 text-gold-400 shadow-[0_0_15px_rgba(212,175,55,0.1)]">
                <Crown size={28} className="drop-shadow-[0_0_8px_rgba(212,175,55,0.4)] animate-pulse" />
              </div>
              
              <h3 className="text-xl font-bold text-white mb-1 tracking-tight">חברות פרימיום במעבדה</h3>
              <p className="text-gold-400 text-xs font-semibold mb-6">גישה מורחבת לחוקרים רשומים בלבד</p>
              
              <div className="w-full text-right bg-white/5 border border-white/5 rounded-xl p-4 mb-6 space-y-3.5">
                <div className="flex items-start gap-2.5">
                  <span className="text-gold-400 text-sm mt-0.5">✦</span>
                  <span className="text-mystic-200 text-xs leading-normal">גישה בלתי מוגבלת לכל מרחבי הסמלים והאסטרולוגיה</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="text-gold-400 text-sm mt-0.5">✦</span>
                  <span className="text-mystic-200 text-xs leading-normal">ניתוחי עומק מבוססי AI לשאלות מורכבות של יחסי גומלין</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="text-gold-400 text-sm mt-0.5">✦</span>
                  <span className="text-mystic-200 text-xs leading-normal">תעדוף משאבים וזמני תגובה מהירים ביותר במודלים מתקדמים</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="text-gold-400 text-sm mt-0.5">✦</span>
                  <span className="text-mystic-200 text-xs leading-normal">שמירת מפות ללא הגבלה והשוואת מפות סימבוליות מתקדמת</span>
                </div>
              </div>
              
              <button
                disabled
                className="w-full py-2.5 bg-mystic-900/60 text-gold-400/50 font-bold rounded-xl border border-gold-500/10 cursor-not-allowed select-none text-xs flex items-center justify-center gap-1.5"
              >
                <Crown size={14} className="opacity-50" />
                <span>השירות יהיה זמין בקרוב</span>
              </button>
            </div>
          </div>
        </Modal>
        {!isChatPage && (
          <div className="fixed bottom-4 left-4 z-40 flex flex-row items-center gap-3 pointer-events-auto" dir="ltr">
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const now = Date.now();
                  if (now - lastNavTimeRef.current < 500) return;
                  lastNavTimeRef.current = now;
                  const currentParams = new URLSearchParams(window.location.search);
                  const activeMapId = currentParams.get("id");
                  navigate(activeMapId ? `/chat?id=${activeMapId}` : "/chat");
                }}
                aria-label="צ'אט ייעוץ AI"
                onMouseEnter={() => setShowChatTooltip(true)}
                onMouseLeave={() => setShowChatTooltip(false)}
                className="w-9 h-9 rounded-full flex items-center justify-center border border-yellow-600/30 bg-yellow-500 hover:bg-yellow-400 active:scale-95 transition-all duration-300 cursor-pointer shadow-[0_0_15px_rgba(234,179,8,0.3)] animate-fade-in"
              >
                <Bot className="w-5.5 h-5.5 text-purple-950 transition-colors duration-300" />
              </button>
              {showChatTooltip && (
                <div className="absolute bottom-11 left-0 bg-black/90 backdrop-blur-md text-[10px] text-gold-200/90 px-2.5 py-1 rounded border border-gold-500/20 shadow-xl whitespace-nowrap z-50 animate-fade-in" dir="rtl">
                  ייעוץ AI
                </div>
              )}
            </div>
          </div>
        )}
        {!isChatPage && <Header />}
        <SchemaJSONLD
          type="Organization"
          data={{
            name: "Spirit Research Lab",
            url: "https://spirit-research-lab.com",
            logo: "https://spirit-research-lab.com/logo.png",
          }}
        />

        <main
          data-no-swipe={isChatPage ? "true" : undefined}
          className={isChatPage ? "w-full h-screen overflow-hidden" : `container mx-auto px-4 ${pathname === "/" ? "" : "py-6"} overflow-x-hidden`}
          {...swipeHandlers}
        >
          <ErrorBoundary variant="full-page">
            <Suspense fallback={<LoadingFallback />}>
              <AnimatePresence mode="popLayout" initial={false} custom={direction}>
                <motion.div
                  key={pathname}
                  custom={direction}
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  className="w-full text-start"
                >
                  <Routes location={location}>
                    <Route path="/" element={<LandingPage />} />

                    {/* Explicit route if they want to force calculator view */}
                    <Route path="/calculator" element={<Calculator />} />

                    <Route path="/saved-maps" element={<SavedMaps />} />
                    <Route path="/account-settings" element={<AccountSettings />} />
                    <Route path="/compare-maps" element={<CompareMaps />} />
                    <Route path="/sky-state" element={<SkyState />} />
                    {/* TurGradientPlayground omitted as file was not found during rebase resolution */}
                    <Route path="/knowledge-center" element={<KnowledgeCenter />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-use" element={<TermsOfUse />} />
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute requireAdmin>
                          <AdminLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<CommandCenter />} />
                    </Route>

                    <Route
                      path="/chat"
                      element={<ChatPage />}
                    />


                    {/* Payment popup close target — loaded by Grow success_url */}
                    <Route path="/payment-success" element={<PaymentSuccessPopup />} />
                    {/* SEO & UX: Intercept dead links with a toast and redirect */}
                    <Route path="*" element={<NotFoundRedirect />} />
                  </Routes>
                </motion.div>
              </AnimatePresence>
            </Suspense>
          </ErrorBoundary>
        </main>

        {!isChatPage && <Footer />}
      </div>
    </CalculatorProvider>
  );
}

export default App;
